import { Inject, Injectable, LOCALE_ID, NgZone, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { LICENCE } from 'src/app/global.tokens';
import { BaseHttpFirebaseService } from '../http/base-http-firebase.service';
import { DetectDeviceService } from '../utils/detect-device.service';
import { JourneyPosthogService } from './journey-posthog.service';
import {
  JourneyEvent,
  JourneyEventsRequest,
  JourneyEventsResponse,
  JourneyKind,
  JourneyScope,
  JourneyState,
} from './journey.models';

const STATE_KEY = 'journey:state';
const BUFFER_KEY = 'journey:buffer';
const SCOPE_KEY = 'journey:scope';
const DISABLED_UNTIL_KEY = 'journey:disabled_until';
const REGISTER_CLICKED_KEY = 'journey:register_clicked_at';
const REGISTER_TO_DEPOSIT_MAX_MS = 3600 * 1000;
const EVENTS_URL = '/ui/clientapp/journey/events';
const BEACON_URL = '/ui/clientapp/journey/beacon';
const FLUSH_MS = 3000;
const MAX_BUFFER = 200;
const MAX_BATCH = 50;
const MAX_BEACON_EVENTS = 50;
const MAX_BEACON_BODY_CHARS = 60000;
const DEPOSIT_JOURNEY_IDLE_MS = 24 * 3600 * 1000;
const DISABLED_MS = 24 * 3600 * 1000;
const IMMEDIATE_EVENTS = new Set(['registration_ok', 'deposit_clicked', 'deposit_response', 'payment_redirect']);

@Injectable({ providedIn: 'root' })
export class JourneyTrackerService {
  private readonly PASSIVE_EVENTS = new Set(['page_hidden', 'page_visible']);
  private readonly isBrowser: boolean;
  private state: JourneyState | null = null;
  private buffer: JourneyEvent[] = [];
  private scope: JourneyScope = 'first_deposit';
  private token: string | null = null;
  private uid: string | null = null;
  private disabled = false;
  private authResolved = false;
  private inFlight = false;
  private flushHandle: ReturnType<typeof setTimeout> | null = null;
  private flushDue = 0;
  private debounceHandles = new Map<string, ReturnType<typeof setTimeout>>();

  constructor(
    private afAuth: AngularFireAuth,
    private http: BaseHttpFirebaseService,
    private detectDeviceService: DetectDeviceService,
    private posthog: JourneyPosthogService,
    private zone: NgZone,
    @Inject(LICENCE) private licence: string,
    @Inject(LOCALE_ID) private locale: string,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (!this.isBrowser) {
      return;
    }
    const state = this.read<JourneyState>(STATE_KEY);
    this.state = state && typeof state === 'object' && typeof state.id === 'string' ? state : null;
    const buffer = this.read<JourneyEvent[]>(BUFFER_KEY);
    this.buffer = Array.isArray(buffer) ? buffer : [];
    this.scope = this.read<JourneyScope>(SCOPE_KEY) || 'first_deposit';
    const disabledUntil = this.read<number>(DISABLED_UNTIL_KEY);
    if (typeof disabledUntil === 'number' && disabledUntil > Date.now()) {
      this.disabled = true;
    }
    this.afAuth.authState.subscribe(user => {
      const cameFromAnonymous = this.authResolved && !this.uid;
      this.uid = user?.uid || null;
      this.authResolved = true;
      this.bindUser(cameFromAnonymous && !!user);
      this.scheduleFlush(0);
    });
    this.afAuth.idToken.subscribe(token => {
      this.token = token;
      this.scheduleFlush(0);
    });
    this.zone.runOutsideAngular(() => {
      window.addEventListener('pagehide', () => {
        if (this.buffer.some(event => !this.PASSIVE_EVENTS.has(event.type))) {
          this.flushNow();
        }
      });
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.track('page_hidden');
          if (this.buffer.some(event => !this.PASSIVE_EVENTS.has(event.type))) {
            this.flushNow();
          }
        } else {
          this.track('page_visible');
        }
      });
    });
  }

  track(type: string, data: Record<string, unknown> = {}): void {
    try {
      if (!this.isBrowser || !this.ensureJourney(type)) {
        return;
      }
      const now = Date.now();
      const event: JourneyEvent = {
        id: this.newId(),
        type,
        client_at: new Date(now).toISOString(),
        ms_since_start: Math.max(0, now - this.state.startedAt),
        url: (location.pathname + location.search).slice(0, 512),
        data,
      };
      this.posthog.capture(type, {
        ...data,
        journey_id: this.state.id,
        journey_kind: this.state.kind,
        journey_event_id: event.id,
        ms_since_start: event.ms_since_start,
        url: event.url,
        entry_url: this.state.entryUrl,
        licence: this.licence,
      });
      this.state.lastAt = now;
      if (this.disabled) {
        this.write(STATE_KEY, this.state);
        return;
      }
      this.buffer.push(event);
      if (this.buffer.length > MAX_BUFFER) {
        this.buffer.splice(0, this.buffer.length - MAX_BUFFER);
      }
      this.persist();
      if (!this.PASSIVE_EVENTS.has(type)) {
        this.scheduleFlush(IMMEDIATE_EVENTS.has(type) ? 0 : FLUSH_MS);
      }
    } catch {
    }
  }

  markRegisterClicked(): void {
    if (!this.isBrowser) {
      return;
    }
    const existing = this.read<number>(REGISTER_CLICKED_KEY);
    if (existing && Date.now() - existing < REGISTER_TO_DEPOSIT_MAX_MS) {
      return;
    }
    this.write(REGISTER_CLICKED_KEY, Date.now());
  }

  trackDepositReadyAfterRegister(data: Record<string, unknown> = {}): void {
    if (!this.isBrowser) {
      return;
    }
    const clickedAt = this.read<number>(REGISTER_CLICKED_KEY);
    if (!clickedAt) {
      return;
    }
    this.write(REGISTER_CLICKED_KEY, null);
    const ms = Date.now() - clickedAt;
    if (ms < 0 || ms > REGISTER_TO_DEPOSIT_MAX_MS) {
      return;
    }
    this.track('deposit_ready_after_register', { ms_since_register: ms, ...data });
  }

  trackDebounced(type: string, data: Record<string, unknown> = {}, delayMs = 800): void {
    if (!this.isBrowser) {
      return;
    }
    clearTimeout(this.debounceHandles.get(type));
    this.zone.runOutsideAngular(() => {
      this.debounceHandles.set(type, setTimeout(() => {
        try {
          this.debounceHandles.delete(type);
          this.track(type, data);
        } catch {
        }
      }, delayMs));
    });
  }

  flushNow(): void {
    try {
      if (!this.canSend()) {
        return;
      }
      let count = Math.min(MAX_BEACON_EVENTS, this.buffer.length);
      let events = this.buffer.slice(0, count);
      let body = JSON.stringify({ ...this.payload(events), token: this.token });
      while (body.length > MAX_BEACON_BODY_CHARS && count > 1) {
        count = Math.max(1, Math.floor(count / 2));
        events = this.buffer.slice(0, count);
        body = JSON.stringify({ ...this.payload(events), token: this.token });
      }
      if (body.length > MAX_BEACON_BODY_CHARS) {
        this.buffer.splice(0, 1);
        this.persist();
        return;
      }
      this.buffer.splice(0, count);
      let queued = false;
      try {
        queued = navigator.sendBeacon(this.http.siteApiOrigin + BEACON_URL, new Blob([body], { type: 'text/plain' }));
      } catch {
        queued = false;
      }
      if (!queued) {
        this.buffer.unshift(...events);
        this.buffer.splice(0, Math.max(0, this.buffer.length - MAX_BUFFER));
      }
      this.persist();
    } catch {
    }
  }

  private ensureJourney(type: string): boolean {
    if (this.state && this.uid && this.state.username && this.state.username !== this.uid) {
      this.reset();
    }
    const isIdleDepositJourney = this.state?.kind === 'deposit' && Date.now() - this.state.lastAt > DEPOSIT_JOURNEY_IDLE_MS;
    if (type === 'signup_opened') {
      if (!this.state || this.state.closed || !!this.state.username || isIdleDepositJourney) {
        this.start('first_deposit');
      }
      return true;
    }
    if (this.state && !this.state.closed && !isIdleDepositJourney) {
      return true;
    }
    if (type !== 'deposit_page_opened') {
      return false;
    }
    if (!this.state) {
      this.start('first_deposit');
      return true;
    }
    if (this.scope === 'all_deposits') {
      this.start('deposit');
      return true;
    }
    return false;
  }

  private start(kind: JourneyKind): void {
    const now = Date.now();
    this.buffer = [];
    this.state = {
      id: this.newId(),
      kind,
      username: this.uid,
      startedAt: now,
      lastAt: now,
      entryUrl: (location.pathname + location.search).slice(0, 512),
      closed: false,
    };
    this.persist();
  }

  private reset(): void {
    this.state = null;
    this.buffer = [];
    this.persist();
  }

  private bindUser(emitLogin: boolean): void {
    if (!this.state || !this.uid) {
      return;
    }
    if (this.state.username && this.state.username !== this.uid) {
      this.reset();
      return;
    }
    if (!this.state.username) {
      this.state.username = this.uid;
      this.persist();
      if (emitLogin) {
        this.track('login_done');
      }
    }
  }

  private canSend(): boolean {
    return this.isBrowser && !this.disabled && !!this.token && !!this.uid
      && !!this.state && !this.state.closed && this.buffer.length > 0;
  }

  private scheduleFlush(delayMs: number): void {
    if (!this.isBrowser) {
      return;
    }
    const due = Date.now() + delayMs;
    if (this.flushHandle && this.flushDue <= due) {
      return;
    }
    clearTimeout(this.flushHandle);
    this.flushDue = due;
    this.zone.runOutsideAngular(() => {
      this.flushHandle = setTimeout(() => {
        this.flushHandle = null;
        this.flush();
      }, delayMs);
    });
  }

  private flush(): void {
    if (this.inFlight) {
      this.scheduleFlush(FLUSH_MS);
      return;
    }
    this.bindUser(false);
    if (!this.canSend()) {
      return;
    }
    const journeyId = this.state.id;
    const events = this.buffer.splice(0, MAX_BATCH);
    this.persist();
    this.inFlight = true;
    this.http.postOnce<JourneyEventsRequest, JourneyEventsResponse>(EVENTS_URL, this.payload(events)).subscribe({
      next: resp => {
        this.inFlight = false;
        this.applyResponse(resp, journeyId);
        if (this.buffer.length) {
          this.scheduleFlush(0);
        }
      },
      error: (error: { status?: number } | undefined) => {
        this.inFlight = false;
        const shouldRequeue = error?.status === 0 || error?.status === undefined || (error?.status ?? 0) >= 500;
        if (shouldRequeue && this.state?.id === journeyId) {
          this.buffer.unshift(...events);
          this.buffer.splice(0, Math.max(0, this.buffer.length - MAX_BUFFER));
          this.persist();
        }
      },
    });
  }

  private applyResponse(resp: JourneyEventsResponse, journeyId: string): void {
    if (!resp?.enabled) {
      this.disabled = true;
      this.reset();
      this.write(DISABLED_UNTIL_KEY, Date.now() + DISABLED_MS);
      return;
    }
    this.scope = resp.scope === 'all_deposits' ? 'all_deposits' : 'first_deposit';
    this.write(SCOPE_KEY, this.scope);
    if (resp.closed && this.state?.id === journeyId) {
      this.state.closed = true;
      this.buffer = [];
      this.persist();
    }
  }

  private payload(events: JourneyEvent[]): JourneyEventsRequest {
    return {
      journey_id: this.state.id,
      kind: this.state.kind,
      licence: this.licence,
      entry_url: this.state.entryUrl,
      started_at: new Date(this.state.startedAt).toISOString(),
      device: {
        ua: navigator.userAgent.slice(0, 512),
        mobile: !this.detectDeviceService.isDesktop(),
        lang: (this.locale || '').slice(0, 16),
        screen: `${screen.width}x${screen.height}`,
      },
      events,
    };
  }

  private newId(): string {
    try {
      return crypto.randomUUID();
    } catch {
      return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
    }
  }

  private persist(): void {
    this.write(STATE_KEY, this.state);
    this.write(BUFFER_KEY, this.buffer);
  }

  private read<T>(key: string): T | null {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) as T : null;
    } catch {
      return null;
    }
  }

  private write(key: string, value: unknown): void {
    try {
      if (value === null || value === undefined) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, JSON.stringify(value));
      }
    } catch {
    }
  }
}
